import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { lazy, Suspense } from 'react'
import './index.css'
import { ToastProvider } from './components/Toast'
import { FeatureGuard } from './components/FeatureGuard'
import { PlatformSessionProvider } from './platform/session'
import { getActiveToken } from './utils/authSession'
import { ensureMediaToken } from './utils/mediaToken'
import App from './App.tsx'
import Admin from './pages/Admin.tsx'
import FAQPage from './pages/FAQPage.tsx'
import WorksPage from './pages/WorksPage.tsx'
import GalleryPage from './pages/GalleryPage.tsx'
import UserCenterLayout from './pages/user/UserCenterLayout.tsx'
import OverviewPage from './pages/user/OverviewPage.tsx'
import CreditsPage from './pages/user/CreditsPage.tsx'
import InvitePage from './pages/user/InvitePage.tsx'
import SettingsPage from './pages/user/SettingsPage.tsx'
import LlmTokensPage from './pages/LlmTokensPage.tsx'
import PhotoRestorePage from './apps/photoRestore/PhotoRestorePage.tsx'
import IterationHistoryPage from './pages/IterationHistoryPage.tsx'
import ApiDocsPage from './pages/ApiDocsPage.tsx'
import VideoApiDocsPage from './pages/VideoApiDocsPage.tsx'
import PaymentResultPage from './pages/PaymentResultPage.tsx'
import EcommerceReplicatePage from './apps/ecommerceReplicate/EcommerceReplicatePage.tsx'
import EcommerceSuitePage from './apps/ecommerceSuite/EcommerceSuitePage.tsx'
import ImpersonationPage from './pages/ImpersonationPage.tsx'
import InviteLinkRedirect from './pages/InviteLinkRedirect.tsx'
import PptPage from './apps/ppt/PptPage.tsx'
import AppsPage from './pages/AppsPage.tsx'
import { VideoPage } from './baselines/video'
import { DirectorWorkbench } from './apps/director/DirectorWorkbench'
import { DirectorSharePage } from './apps/director/DirectorSharePage'

// 画布页面用懒加载，避免主包变重
const BoardPage = lazy(() => import('./apps/board/BoardPage.tsx'))

// 已登录会话在首帧渲染前预取短时媒体令牌（<img>/<video> 无法带 Authorization 头，
// 且账号 JWT 已禁止进 URL），2 秒超时兜底避免接口异常阻塞白屏
async function bootstrap() {
  if (getActiveToken()) {
    await Promise.race([ensureMediaToken(), new Promise((resolve) => setTimeout(resolve, 2000))]);
  }
  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <ToastProvider>
        <PlatformSessionProvider>
          <BrowserRouter>
        <Routes>
          <Route path="/" element={<App />} />
          <Route path="/r/:code" element={<InviteLinkRedirect />} />
          <Route path="/apps" element={<AppsPage />} />
          <Route path="/video" element={<FeatureGuard feature="videoEnabled" featureName="视频生成"><VideoPage /></FeatureGuard>} />
          <Route path="/apps/ppt" element={<FeatureGuard feature="pptEnabled" featureName="PPT 生成"><PptPage /></FeatureGuard>} />
          <Route path="/apps/ppt/:projectId" element={<FeatureGuard feature="pptEnabled" featureName="PPT 生成"><PptPage /></FeatureGuard>} />
          <Route path="/apps/director" element={<FeatureGuard feature="directorEnabled" featureName="导演模式"><DirectorWorkbench /></FeatureGuard>} />
          <Route path="/apps/director/:projectId" element={<FeatureGuard feature="directorEnabled" featureName="导演模式"><DirectorWorkbench /></FeatureGuard>} />
          <Route path="/share/director/:token" element={<DirectorSharePage />} />
          <Route
            path="/apps/board"
            element={
              <FeatureGuard feature="canvasEnabled" featureName="无限画布">
                <Suspense fallback={<div className="flex h-screen items-center justify-center text-sm text-slate-500">画布加载中...</div>}>
                  <BoardPage />
                </Suspense>
              </FeatureGuard>
            }
          />
          <Route
            path="/apps/board/:projectId"
            element={
              <FeatureGuard feature="canvasEnabled" featureName="无限画布">
                <Suspense fallback={<div className="flex h-screen items-center justify-center text-sm text-slate-500">画布加载中...</div>}>
                  <BoardPage />
                </Suspense>
              </FeatureGuard>
            }
          />
          <Route path="/admin" element={<Admin />} />
          <Route path="/faq" element={<FAQPage />} />
          <Route path="/templates" element={<Navigate to="/" replace />} />
          <Route path="/works" element={<WorksPage />} />
          <Route path="/gallery" element={<GalleryPage />} />
          <Route path="/user" element={<UserCenterLayout />}>
            <Route index element={<Navigate to="/user/overview" replace />} />
            <Route path="overview" element={<OverviewPage />} />
            <Route path="credits" element={<CreditsPage />} />
            <Route path="invite" element={<InvitePage />} />
            <Route path="settings" element={<SettingsPage />} />
          </Route>
          <Route path="/llm/tokens" element={<LlmTokensPage />} />
          <Route path="/apps/photo-restore" element={<FeatureGuard feature="photoRestoreEnabled" featureName="照片修复"><PhotoRestorePage /></FeatureGuard>} />
          <Route path="/iterations" element={<IterationHistoryPage />} />
          <Route path="/api-docs" element={<ApiDocsPage />} />
          <Route path="/video-api-docs" element={<VideoApiDocsPage />} />
          <Route path="/apps/ecommerce-replicate" element={<FeatureGuard feature="ecommerceReplicateEnabled" featureName="电商复刻"><EcommerceReplicatePage /></FeatureGuard>} />
          <Route path="/apps/ecommerce-replicate/:taskId" element={<FeatureGuard feature="ecommerceReplicateEnabled" featureName="电商复刻"><EcommerceReplicatePage /></FeatureGuard>} />
          <Route path="/apps/ecommerce-suite" element={<FeatureGuard feature="ecommerceSuiteEnabled" featureName="电商套图"><EcommerceSuitePage /></FeatureGuard>} />
          <Route path="/apps/ecommerce-suite/:projectId" element={<FeatureGuard feature="ecommerceSuiteEnabled" featureName="电商套图"><EcommerceSuitePage /></FeatureGuard>} />
          <Route path="/payment/return" element={<PaymentResultPage />} />
          <Route path="/payment/success" element={<PaymentResultPage />} />
          <Route path="/payment/pending" element={<PaymentResultPage />} />
          <Route path="/payment/error" element={<PaymentResultPage />} />
          <Route path="/impersonate" element={<ImpersonationPage />} />
          <Route path="/register" element={<App />} />
          <Route path="*" element={<App />} />
        </Routes>
      </BrowserRouter>
      </PlatformSessionProvider>
    </ToastProvider>
  </StrictMode>,
  )
}

void bootstrap();
